/**
 * @export
 * @class Navigation
 */
import { playVideo } from "../../helpers/play-video";
import { Toggler } from "../../helpers/toggler";
import { forEach } from "../../helpers/utilities";

export class Navigation {
  private target: HTMLElement;
  private video: HTMLVideoElement;
  private activeHover!: Element;
  private isAnimating = false;
  private mainToggleBtn = <HTMLButtonElement>(
    document.getElementById("js-site-nav__toggle")
  );
  private togglerInstance!: Toggler;

  /**
   * Creates an instance of Navigation.
   *
   * @memberof Navigation
   */
  constructor() {
    this.target = <HTMLElement>document.getElementById("js-site-nav");
    this.togglerInstance = new Toggler(this.target, "c-site-nav--is-visible");
    this.video = <HTMLVideoElement>(
      document.getElementById("js-site-nav__video")
    );

    if (this.target) this.bindEvents();
  }

  /**
   * Bind the events which make this work.
   *
   * @private
   * @memberof Navigation
   */
  private bindEvents(): void {
    // Toggle the nav when clicking the main menu button
    this.mainToggleBtn.addEventListener("click", () => {
      this.toggle();
    });

    // Dropdowns
    const dropdownBtns = this.target.querySelectorAll(".js-main-menu__button");
    if (dropdownBtns) {
      forEach(dropdownBtns, ({}, btn: HTMLElement) => {
        btn.addEventListener("click", () => {
          const item = <HTMLElement>btn.parentNode;
          if (item) this.toggleDropdown(item);
        });
      });
    }

    // Close on leave
    const items = this.target.querySelectorAll(".js-main-menu__item");
    this.target.addEventListener("mouseover", (event) => {
      const targetItem = (event.target as Element).closest(
        ".js-main-menu__item"
      );
      this.activeHover = <Element>targetItem;
    });

    // Close all except target item on hover off menu
    this.target.addEventListener("mousemove", () => {
      forEach(items, ({}, item: HTMLElement) => {
        if (item !== this.activeHover) {
          this.closeDropdown(item);
        } else {
          this.openDropdown(item);
        }
      });
    });
  }

  /**
   * Toggles the navigation to it's opposite state
   *
   * @private
   * @memberof Navigation
   */
  private toggle(): void {
    if (this.togglerInstance.isActive) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Opens the navigation
   *
   * @memberof Navigation
   */
  private open(): void {
    // Turn the button plus into a cross
    this.mainToggleBtn.classList.add("o-burger-menu--is-open");

    // Remove the fixed class from the header and stop it being added again
    window.app.modules.siteHeader.triggerDisabled = true;
    window.app.modules.siteHeader.removeBackgroundClass();

    // Toggle the video play state if it exists
    if (this.video) {
      playVideo(this.video);
    }

    // Display the menu
    this.togglerInstance.add();
  }

  /**
   * Closes the navigation
   *
   * @memberof Navigation
   */
  private close(): void {
    // Turn the button cross back into a normal burger menu
    this.mainToggleBtn.blur();
    this.mainToggleBtn.classList.remove("o-burger-menu--is-open");

    // Enable the background toggling in the header again and add it if we're at the right scroll pos
    window.app.modules.siteHeader.triggerDisabled = false;
    window.app.modules.siteHeader.toggleBackground();

    // Toggle the video play state if it exists
    if (this.video) {
      this.video.pause();
    }

    this.togglerInstance.remove();
  }

  /**
   * Toggle the dropdown open and closed
   *
   * @param btn
   */
  private toggleDropdown(item: HTMLElement): void {
    const dropdown = <HTMLElement>item.querySelector(".js-main-menu__dropdown");
    if (dropdown.classList.contains("c-main-menu__dropdown--is-open")) {
      this.closeDropdown(item);
    } else {
      this.openDropdown(item);
    }
  }

  /**
   * Opens the provided item if it's a dropdown
   *
   * @private
   * @memberof Navigation
   */
  private openDropdown(item: Element): void {
    const dropdown = <HTMLElement>item.querySelector(".js-main-menu__dropdown");

    if (dropdown) {
      if (!dropdown.classList.contains("c-main-menu__dropdown--is-open")) {
        const menu = <HTMLElement>item.querySelector(".js-main-menu__sub-menu");
        const menuHeight = menu.clientHeight;
        const btn = item.querySelector(".js-main-menu__button");

        if (menu && menuHeight && btn && !this.isAnimating) {
          this.isAnimating = true;
          btn.classList.add("c-main-menu__button--is-open");

          dropdown.style.height = `${menuHeight}px`;
          setTimeout(() => {
            dropdown.classList.add("c-main-menu__dropdown--is-open");
            dropdown.style.height = "";
            this.isAnimating = false;
          }, 300);
        }
      }
    }
  }

  /**
   * Opens the provided item if it's a dropdown
   *
   * @private
   * @memberof Navigation
   */
  private closeDropdown(item: Element): void {
    const dropdown = <HTMLElement>item.querySelector(".js-main-menu__dropdown");
    if (dropdown) {
      if (dropdown.classList.contains("c-main-menu__dropdown--is-open")) {
        const menu = <HTMLElement>item.querySelector(".js-main-menu__sub-menu");
        const menuHeight = menu.clientHeight;
        const btn = item.querySelector(".js-main-menu__button");

        if (menu && menuHeight && btn && !this.isAnimating) {
          this.isAnimating = true;
          btn.classList.remove("c-main-menu__button--is-open");

          dropdown.style.height = `${menuHeight}px`;
          dropdown.classList.remove("c-main-menu__dropdown--is-open");

          setTimeout(() => {
            dropdown.style.height = "";

            setTimeout(() => {
              this.isAnimating = false;
            }, 300);
          }, 50);
        }
      }
    }
  }
}
